/**
 * UR Brand System — Phase 2 intake engine (shell + routing).
 *
 * Groups the QUESTIONS config into LOGICAL STEPS (`STEPS`), filters to
 * the applicable journey via each question's `when` predicate, renders
 * one step (possibly multiple questions) at a time with Back/Continue,
 * drives autosave via useIntakeSession, and handles Review + Submit.
 *
 * The ~19-step customer journey is realized entirely by this engine +
 * config — no per-screen custom components.
 */
import React, { useMemo, useState, useCallback } from "react";
import { QUESTIONS, SECTIONS, STEPS } from "./intakeConfig";
import { useIntakeSession } from "./useIntakeSession";
import {
  Informational, ShortText, LongText, SingleSelect, MultiSelect,
  MultiSelectText, PerceptionScale, DifferentiationReason, Competitors,
} from "./renderers";
import { Button } from "../components/ui/button";

// -------------------------------------------------------------------------
// Path utils
// -------------------------------------------------------------------------

const readPathValue = (project, path) => {
  if (!project || !path) return undefined;
  if (path === "business_stage" || path === "naming_status" || path === "existing_identity") {
    return project[path];
  }
  const re = /([^[\].]+)|\[(\d+)\]/g;
  const parts = [];
  let m;
  while ((m = re.exec(path)) !== null) parts.push(m[1] ?? Number(m[2]));
  let cur = project;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
};

const unwrap = (v) => (v && typeof v === "object" && "value" in v ? v.value : v);

// -------------------------------------------------------------------------
// Renderer dispatch
// -------------------------------------------------------------------------

const RENDERERS = {
  informational: Informational,
  short_text: ShortText,
  long_text: LongText,
  single_select: SingleSelect,
  multi_select: MultiSelect,
  multi_select_text: MultiSelectText,
  perception_scale: PerceptionScale,
  differentiation_reason: DifferentiationReason,
  competitors: Competitors,
};

// -------------------------------------------------------------------------
// Required-core gate (matches the backend's /submit validation)
// -------------------------------------------------------------------------

const REQUIRED_CORE_PATHS = new Set([
  "business_stage",
  "naming_status",
  "brand_data.offer.offer_description",
  "brand_data.customer.customer_type",
  "brand_data.language.customer_languages",
  "brand_data.project.decision_maker",
  "brand_data.project.contact_name",
]);

const isAnswered = (v) => {
  const value = unwrap(v);
  if (value == null || value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

const isStepComplete = (step, project) => {
  for (const q of step.questions) {
    if (!q.required) continue;
    const v = unwrap(readPathValue(project, q.path));
    if (q.allowUnknown && v === "unknown_stated" && !REQUIRED_CORE_PATHS.has(q.path)) {
      continue;
    }
    if (v === "unknown_stated") return false; // required-core never accepts unknown
    if (!isAnswered(v)) return false;
  }
  return true;
};

// -------------------------------------------------------------------------
// Review view — customer-provided facts only, grouped by UX section
// -------------------------------------------------------------------------

const ReviewView = ({ project, visibleSteps, onEditStep }) => (
  <div className="space-y-6" data-testid="intake-review">
    <p className="text-neutral-400 text-sm">
      Here's what you told us. Edit anything you'd like before you submit.
    </p>
    {SECTIONS.map((section) => {
      const steps = visibleSteps.filter((s) => s.section === section.id);
      const rows = [];
      for (const step of steps) {
        for (const q of step.questions) {
          if (!q.path) continue;
          rows.push({ step, q });
        }
      }
      if (rows.length === 0) return null;
      return (
        <div key={section.id} className="rounded-lg border border-neutral-800 overflow-hidden">
          <div className="px-4 py-2 bg-neutral-900 text-xs uppercase tracking-widest text-orange-400">
            {section.title}
          </div>
          <div className="divide-y divide-neutral-800">
            {rows.map(({ step, q }) => {
              const raw = readPathValue(project, q.path);
              const v = unwrap(raw);
              const display = Array.isArray(v)
                ? v.join(", ")
                : (v == null || v === "" ? "—" : v === "unknown_stated" ? "I don't know yet" : String(v));
              return (
                <div key={q.id} className="px-4 py-3 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-neutral-500">{q.title}</div>
                    <div className="text-sm text-neutral-100 mt-1 break-words">{display}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onEditStep(step.id)}
                    data-testid={`intake-review-edit-${q.id}`}
                    className="text-xs text-orange-400 hover:text-orange-300 shrink-0"
                  >
                    Edit
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);

// -------------------------------------------------------------------------
// Main engine
// -------------------------------------------------------------------------

export default function IntakeEngine() {
  const {
    hasSession, project, loading, saving, savedAt, error,
    startNew, reset, setFieldByPath, submit,
  } = useIntakeSession();

  const [stepId, setStepId] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Visible steps — question `when` predicates are evaluated against the
  // current project on every render, so changing a routing answer
  // immediately refilters the journey (steps with zero visible questions
  // drop out entirely).
  const visibleSteps = useMemo(() => {
    if (!project) return [];
    const byStep = new Map();
    for (const q of QUESTIONS) {
      if (q.when && !q.when(project)) continue;
      if (!byStep.has(q.step)) byStep.set(q.step, []);
      byStep.get(q.step).push(q);
    }
    return STEPS
      .filter((s) => byStep.has(s.id))
      .map((s) => ({ ...s, questions: byStep.get(s.id) }));
  }, [project]);

  const currentIndex = useMemo(() => {
    if (!stepId) return 0;
    const i = visibleSteps.findIndex((s) => s.id === stepId);
    return i >= 0 ? i : 0;
  }, [stepId, visibleSteps]);

  const current = visibleSteps[currentIndex];
  const sectionIndex = SECTIONS.findIndex((s) => s.id === current?.section);
  const progress = visibleSteps.length > 0
    ? Math.round(((currentIndex + 1) / visibleSteps.length) * 100) : 0;

  const goPrev = useCallback(() => {
    const prev = visibleSteps[currentIndex - 1];
    if (prev) setStepId(prev.id);
  }, [visibleSteps, currentIndex]);

  const goNext = useCallback(() => {
    const next = visibleSteps[currentIndex + 1];
    if (next) setStepId(next.id);
  }, [visibleSteps, currentIndex]);

  const canContinue = useMemo(() => {
    if (!current) return false;
    if (["review", "submit"].includes(current.questions[0]?.type)) return true;
    return isStepComplete(current, project);
  }, [current, project]);

  const doSubmit = useCallback(async () => {
    setSubmitting(true); setSubmitError(null);
    const res = await submit();
    setSubmitting(false);
    if (!res || !res.ok) {
      const d = res?.detail;
      if (d && typeof d === "object" && Array.isArray(d.fields)) {
        setSubmitError(`Please complete: ${d.fields.join(", ")}`);
      } else {
        setSubmitError(typeof d === "string" ? d : "Could not submit. Please try again.");
      }
    }
  }, [submit]);

  // --- gate / loading / landing --------------------------------------------

  if (loading && !project) {
    return <Shell><div className="text-neutral-400 text-sm">Loading your intake…</div></Shell>;
  }

  if (!hasSession) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold text-white">UR Brand System intake</h1>
        <p className="text-neutral-400 mt-3 text-sm">
          A 10-15 minute, structured conversation. Save and resume at any time.
        </p>
        {error && <p className="text-red-400 text-sm mt-3" data-testid="intake-error">{error}</p>}
        <div className="mt-6">
          <Button onClick={startNew} data-testid="intake-start" className="bg-orange-500 hover:bg-orange-600 text-black font-medium">
            Start intake
          </Button>
        </div>
      </Shell>
    );
  }

  // --- submitted state -----------------------------------------------------

  if (project?.project_status === "INTAKE_COMPLETE") {
    return (
      <Shell>
        <div data-testid="intake-done">
          <h1 className="text-2xl font-semibold text-white">Thanks — your intake is in.</h1>
          <p className="text-neutral-400 mt-3 text-sm">
            Submitted {project.submitted_at && new Date(project.submitted_at).toLocaleString()}.
            Ur Designs will review your answers and reach out with next steps.
          </p>
          <button
            type="button"
            onClick={reset}
            data-testid="intake-start-another"
            className="mt-6 text-xs text-neutral-500 hover:text-white underline"
          >
            Start a different intake on this device
          </button>
        </div>
      </Shell>
    );
  }

  // --- active intake -------------------------------------------------------

  if (!current) {
    return <Shell><div className="text-neutral-400 text-sm">No questions to show.</div></Shell>;
  }

  const primaryType = current.questions[0]?.type;
  const isReview = primaryType === "review";
  const isSubmit = primaryType === "submit";
  const singleQuestion = current.questions.length === 1;
  const heading = singleQuestion ? current.questions[0].title : current.title;
  const headingHelp = singleQuestion ? current.questions[0].help : current.help;

  return (
    <Shell>
      {/* progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <div data-testid="intake-section">
            {SECTIONS[sectionIndex]?.title}
            <span className="mx-2 text-neutral-700">·</span>
            Step {currentIndex + 1} of {visibleSteps.length}
          </div>
          <SaveIndicator saving={saving} savedAt={savedAt} error={error} />
        </div>
        <div className="h-1 bg-neutral-900 rounded mt-2 overflow-hidden">
          <div
            data-testid="intake-progress"
            className="h-full bg-orange-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <h2 className="text-xl sm:text-2xl font-semibold text-white" data-testid="intake-title">
        {heading}
      </h2>
      {headingHelp && (
        <p className="text-neutral-400 text-sm mt-2" data-testid="intake-help">{headingHelp}</p>
      )}

      <div className="mt-6 space-y-8" data-testid={`intake-step-${current.id}`}>
        {isReview ? (
          <ReviewView
            project={project}
            visibleSteps={visibleSteps}
            onEditStep={(id) => setStepId(id)}
          />
        ) : isSubmit ? (
          <div className="space-y-4" data-testid="intake-submit-step">
            <p className="text-neutral-300 text-sm">
              Ready to submit? You can edit from the Review step before continuing.
            </p>
            {submitError && (
              <p className="text-red-400 text-sm" data-testid="intake-submit-error">{submitError}</p>
            )}
          </div>
        ) : (
          current.questions.map((q) => {
            const Renderer = RENDERERS[q.type];
            if (!Renderer) return null;
            return (
              <div key={q.id} data-testid={`intake-question-${q.id}`}>
                {!singleQuestion && (
                  <div className="mb-3">
                    <div className="text-sm font-medium text-neutral-100">{q.title}</div>
                    {q.help && <div className="text-xs text-neutral-500 mt-1">{q.help}</div>}
                  </div>
                )}
                <Renderer
                  question={q}
                  value={readPathValue(project, q.path)}
                  allowUnknown={q.allowUnknown}
                  onChange={(v) => setFieldByPath(q.path, v)}
                />
              </div>
            );
          })
        )}
      </div>

      {/* nav */}
      <div className="mt-8 flex items-center justify-between">
        <Button
          type="button"
          variant="secondary"
          onClick={goPrev}
          disabled={currentIndex === 0}
          data-testid="intake-back"
        >
          Back
        </Button>
        {isSubmit ? (
          <Button
            type="button"
            onClick={doSubmit}
            disabled={submitting}
            data-testid="intake-submit"
            className="bg-orange-500 hover:bg-orange-600 text-black font-medium"
          >
            {submitting ? "Submitting…" : "Submit intake"}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={goNext}
            disabled={!canContinue || currentIndex >= visibleSteps.length - 1}
            data-testid="intake-continue"
            className="bg-orange-500 hover:bg-orange-600 text-black font-medium"
          >
            {singleQuestion ? (current.questions[0].continueLabel || "Continue") : "Continue"}
          </Button>
        )}
      </div>
    </Shell>
  );
}

// -------------------------------------------------------------------------
// Shell (brand-consistent container, mobile-first)
// -------------------------------------------------------------------------

const Shell = ({ children }) => (
  <div className="min-h-screen bg-black text-neutral-100">
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <div className="text-[11px] uppercase tracking-widest text-orange-500 mb-6">
        UR Designs · Brand Intake
      </div>
      {children}
    </div>
  </div>
);

const SaveIndicator = ({ saving, savedAt, error }) => {
  if (error) return <span data-testid="intake-save-state" className="text-red-400">⚠ {error}</span>;
  if (saving) return <span data-testid="intake-save-state" className="text-neutral-400">Saving…</span>;
  if (savedAt) return <span data-testid="intake-save-state" className="text-neutral-500">Saved</span>;
  return <span data-testid="intake-save-state" className="text-neutral-700">—</span>;
};
